import { useEffect, useRef } from "react";
import { ActionButton } from "../../../components/Buttons";
import { validateCardQuestion } from "../refineResultState";

function RefineEditWordingModal({
  isOpen = true,
  value,
  savedValue,
  onChange,
  onClose,
  onSave,
  saving = false,
  savingLabel = "Saving…",
  error = "",
}) {
  const textareaRef = useRef(null);
  const validation = validateCardQuestion(value, savedValue);
  const titleId = "edit-wording-title";
  const descriptionId = "edit-wording-description";
  const errorId = "edit-wording-error";

  useEffect(() => {
    if (!isOpen) return undefined;

    textareaRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const characterCount = [...(value || "")].length;
  const describedBy = error
    ? `${descriptionId} ${errorId}`
    : descriptionId;

  return (
    <div
      className="fixed inset-0 z-[100] flex min-h-[100dvh] items-end justify-center overflow-hidden bg-black/38 px-2 pb-0 text-left backdrop-blur-[1px] sm:items-center sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <form
        className="relative z-10 w-full max-w-md overflow-hidden rounded-t-[1.5rem] bg-[#fffdf9] text-[#071015] shadow-[0_22px_70px_rgba(0,0,0,0.28)] sm:rounded-[1.5rem]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        onSubmit={(event) => {
          event.preventDefault();
          if (validation.valid && !saving) onSave();
        }}
      >
        <div className="max-h-[82dvh] overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 sm:max-h-[calc(100vh-3rem)] sm:px-5 sm:pb-5">
          <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-black/14 sm:mb-4" />

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2
                id={titleId}
                className="max-w-[17rem] text-xl font-black leading-tight text-[#071015] sm:text-2xl"
              >
                Edit wording
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label="Close edit wording"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-black/44 transition hover:bg-black/5 hover:text-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#087a34] disabled:pointer-events-none disabled:opacity-50"
            >
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <p
            id={descriptionId}
            className="mt-1.5 text-sm font-medium leading-snug text-[#5c6457]"
          >
            Remember to keep your card to one kind question, and end with a
            question mark. We'll update the translations for you.
          </p>

          <div className="relative mt-5">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              rows={6}
              aria-label="Card wording"
              aria-invalid={!validation.valid && Boolean(value)}
              aria-describedby={describedBy}
              disabled={saving}
              dir="auto"
              className="w-full resize-none rounded-2xl border border-[#307609]/20 bg-white px-4 py-4 pb-9 pr-20 text-base font-bold leading-relaxed text-gray-950 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.9)] outline-none transition focus:border-[#307609]/45 focus:ring-4 focus:ring-[#307609]/10"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            />
            <span
              className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-white/90 px-1 text-xs font-semibold text-gray-500"
              aria-label={`${characterCount} of 280 characters`}
            >
              {characterCount}/280
            </span>
          </div>

          <div className="mt-2 min-h-[1.25rem] text-xs font-semibold text-gray-600">
            <span
              id={errorId}
              className={
                error || (!validation.valid && value) ? "text-[#307609]" : ""
              }
              aria-live="polite"
            >
              {error || (value && !validation.valid ? validation.message : "")}
            </span>
          </div>

          <ActionButton
            type="submit"
            disabled={!validation.valid || saving}
            className="mt-4 w-full"
            icon="check"
            variant="wall"
          >
            {saving ? savingLabel : "Save changes"}
          </ActionButton>

          <ActionButton
            type="button"
            onClick={onClose}
            disabled={saving}
            className="mt-2 w-full"
            variant="ghost"
          >
            Cancel
          </ActionButton>
        </div>
      </form>
    </div>
  );
}

export default RefineEditWordingModal;
